import React, { useEffect, useRef, useState } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import * as turf from "@turf/turf";
import { Box, Typography, alpha } from "@mui/material";
import { SensorsOff } from "@mui/icons-material";

import { glassSx } from "./overlayStyles";

// ✅ Persistent storage for circles outside the component
const circlesByAzimuthRef = {}; // { [azimuthIndex]: { lastSweep, layers } }

const RadarScan = ({
  center,
  radiusKm = 50,
  beamAngle = 3,
  rotationSpeed = 2, // degrees per tick
  trailSteps = 15,
  color = "0,255,0", // RGB string
  ringCount = 3,
  radarData = null,
  showNoData = false, // Show "No Data" overlay if no radarData
}) => {
  const map = useMap();
  const [rotation, setRotation] = useState(0);
  const layerRefs = useRef([]);
  const baseRefs = useRef([]);
  const pulseRef = useRef(null);
  const pixelRefs = useRef([]);
  const sweepCounterRef = useRef(0);
  const lastAzimuthRef = useRef(null);
  const lastDataAtRef = useRef(null);   // when valid radar data last arrived (for "signal lost")

  // Create background glow, rings, and pulse
  // useEffect(() => {
    // const glow = L.circle(center, {
    //   radius: radiusKm * 1000,
    //   color: `rgba(${color}, 0.1)`,
    //   fillColor: `rgba(${color}, 0.05)`,
    //   fillOpacity: 0.1,
    //   weight: 1,
    //   interactive: false,
    // }).addTo(map);
    // baseRefs.current.push(glow);

    // for (let i = 1; i <= ringCount; i++) {
    //   const ring = L.circle(center, {
    //     radius: (i * radiusKm * 1000) / ringCount,
    //     color: `rgba(${color}, 0.25)`,
    //     dashArray: "4, 6",
    //     weight: 1,
    //     interactive: false,
    //   }).addTo(map);
    //   baseRefs.current.push(ring);
    // }

    // const pulse = L.circleMarker(center, {
    //   radius: 6,
    //   color: `rgba(${color}, 0.9)`,
    //   fillColor: `rgba(${color}, 1)`,
    //   fillOpacity: 1,
    //   weight: 1,
    //   interactive: false,
    // }).addTo(map);
    // pulseRef.current = pulse;

    // let pulsePhase = 0;
    // const pulseInterval = setInterval(() => {
    //   pulsePhase += 0.1;
    //   const opacity = 0.5 + 0.5 * Math.sin(pulsePhase);
    //   if (pulseRef.current) {
    //     pulseRef.current.setStyle({ fillOpacity: opacity });
    //   }
    // }, 100);

    // return () => {
    //   baseRefs.current.forEach((layer) => map.removeLayer(layer));
    //   baseRefs.current = [];
    //   if (pulseRef.current) {
    //     map.removeLayer(pulseRef.current);
    //     pulseRef.current = null;
    //   }
    //   clearInterval(pulseInterval);
    // };
  // }, [center, radiusKm, color, ringCount, map]);

  // Draw radarData beam and pixels
  useEffect(() => {
    if (radarData && radarData.azimuth !== undefined && Array.isArray(radarData.pixels)) {
      let azimuth = radarData.azimuth * (360 / 1024);
      let azimuthIndex = Math.round(azimuth) % 360;

      // ✅ Detect full 360° rotation
      if (lastAzimuthRef.current !== null) {
        const diff = azimuthIndex - lastAzimuthRef.current;
        if (diff < -180) {
          sweepCounterRef.current += 1;
        }
      }
      lastAzimuthRef.current = azimuthIndex;

      // Normalize the azimuth layer entry
      let entry = circlesByAzimuthRef[azimuthIndex];
      if (!entry) {
        entry = { lastSweep: -1, layers: [] };
        circlesByAzimuthRef[azimuthIndex] = entry;
      }

      if (entry.lastSweep !== sweepCounterRef.current) {
        entry.layers.forEach(layer => map.removeLayer(layer));
        entry.layers = [];
        entry.lastSweep = sweepCounterRef.current;
      }

      // Remove sweep polygons
      pixelRefs.current.forEach(layer => map.removeLayer(layer));
      pixelRefs.current = [];

      // Draw the beam sector
      const beamStart = azimuth - beamAngle / 2;
      const beamEnd = azimuth + beamAngle / 2;
      const steps = 24;
      const points = [[center.lng, center.lat]];
      for (let j = 0; j <= steps; j++) {
        const angle = beamStart + ((beamEnd - beamStart) * j) / steps;
        const dest = turf.destination([center.lng, center.lat], radiusKm, angle, { units: "kilometers" });
        points.push(dest.geometry.coordinates);
      }
      points.push([center.lng, center.lat]);
      const beamPolygon = turf.polygon([points]);
      const beamLayer = L.geoJSON(beamPolygon, {
        style: {
          color: `rgba(${color}, 0.2)`,
          fillColor: `rgba(${color}, 0.15)`,
          fillOpacity: 0.15,
          weight: 1,
          interactive: false,
        },
      }).addTo(map);
      pixelRefs.current.push(beamLayer);

      // Draw fading trail
      for (let i = 1; i <= trailSteps; i++) {
        const trailAz = azimuth - i * 1;
        const alpha = (1 - i / (trailSteps + 1)).toFixed(2);
        const trailStart = trailAz - beamAngle / 2;
        const trailEnd = trailAz + beamAngle / 2;
        const trailPoints = [[center.lng, center.lat]];
        for (let j = 0; j <= steps; j++) {
          const angle = trailStart + ((trailEnd - trailStart) * j) / steps;
          const dest = turf.destination([center.lng, center.lat], radiusKm, angle, { units: "kilometers" });
          trailPoints.push(dest.geometry.coordinates);
        }
        trailPoints.push([center.lng, center.lat]);
        const trailPoly = turf.polygon([trailPoints]);
        const trailLayer = L.geoJSON(trailPoly, {
          style: {
            color: `rgba(${color}, 0)`,
            fillColor: `rgba(${color}, ${alpha})`,
            fillOpacity: parseFloat(alpha),
            weight: 0,
            interactive: false,
          },
        }).addTo(map);
        pixelRefs.current.push(trailLayer);
      }

      // Draw radar pixel circles
      radarData.pixels.forEach((pixel, i) => {
        const distanceKm = pixel.dis;
        if (!distanceKm) return;

        const angleStart = (radarData.azimuth * 2) / 2048 * 360;
        const angleEnd = angleStart - 1;

        // if pixel.mtiDis is null or undefined, use default 50
        let mtiDis = pixel.mtiDis ?? 50;

        let clr = `rgba(0,255,0,1)`;
        let amp = 0;

        if (pixel.refCh === 2) {
          amp = 30 + pixel.amp * (i < mtiDis ? 5 : 0.7);
          let clrVal2 = 170 + (amp / 2) / 255;
          amp = Math.min(255, amp);
          clr = `rgba(${amp / 2},${amp},${amp},1)`;

        } else if (pixel.refCh === 1) {
          amp = 30 + pixel.amp * (i < mtiDis ? 5 : 1.4);
          let clrVal1 = 200 + (amp) / 255;
          amp = Math.min(255, amp);
          clr = `rgba(0,${amp * 2},0,1)`;

        } else {
          return;
        }

        // Compute start/end points of arc
        const start = turf.destination([center.lng, center.lat], distanceKm, angleStart, { units: "kilometers" });
        const end = turf.destination([center.lng, center.lat], distanceKm, angleEnd, { units: "kilometers" });

        const arcCoords = [
          [start.geometry.coordinates[1], start.geometry.coordinates[0]],
          [end.geometry.coordinates[1], end.geometry.coordinates[0]],
        ];

        // Draw line (small arc segment)
        const arc = L.polyline(arcCoords, {
          color: clr,
          weight: 1,
          opacity: 0.9,
          interactive: false,
        }).addTo(map);

        // Optional zIndex for layering
        // arc.setZIndexOffset(1000);

        entry.layers.push(arc);
      });
    }    
  }, [radarData, center, color, beamAngle, radiusKm, map, trailSteps]);

  useEffect(() => {
    return () => {
      // ✅ This runs ONLY on final unmount
      Object.values(circlesByAzimuthRef).forEach(entry => {
        if (entry && Array.isArray(entry.layers)) {
          entry.layers.forEach(layer => {
            if (layer && map.hasLayer(layer)) map.removeLayer(layer);
          });
        }
      });
      // Clear the global ref if needed
      for (const key in circlesByAzimuthRef) {
        delete circlesByAzimuthRef[key];
      }

      pixelRefs.current.forEach(layer => {
        if (layer && map.hasLayer(layer)) map.removeLayer(layer);
      });
      pixelRefs.current = [];
    };
  }, []);

  const hasData = Boolean(radarData && radarData.azimuth !== undefined && Array.isArray(radarData.pixels));
  if (hasData) lastDataAtRef.current = Date.now();

  if (showNoData && !hasData) {
    return <RadarNoSignal color={color} lastDataAt={lastDataAtRef.current} />;
  }

  return null;
};

// "No data" status for the radar station view: a small card under the unit badge (top center),
// so it never covers the station and its range rings in the middle of the map.
//  - nothing received yet: NO RADAR SIGNAL · waiting for data
//  - data stopped:         SIGNAL LOST · last data at HH:mm:ss
const RadarNoSignal = ({ color, lastDataAt }) => {
  const accent = `rgb(${color})`;
  const lost = Boolean(lastDataAt);
  const warn = "#FFB020";

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        position: "absolute",
        top: 76,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 1000,
        pointerEvents: "none",
        userSelect: "none",
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        pl: 1,
        pr: 2,
        py: 0.75,
        borderRadius: "12px",
        color: "#fff",
        ...glassSx(lost ? warn : accent),
      }}
    >
      {/* Radar icon with a slow idle pulse (no sweep: nothing is being scanned) */}
      <Box
        sx={{
          position: "relative",
          width: 34,
          height: 34,
          flexShrink: 0,
          display: "grid",
          placeItems: "center",
          borderRadius: "50%",
          color: lost ? warn : accent,
          border: `1px solid ${alpha(lost ? warn : accent, 0.5)}`,
          "&::after": {
            content: '""',
            position: "absolute",
            inset: -1,
            borderRadius: "50%",
            border: `1px solid ${lost ? warn : accent}`,
            animation: "radarIdlePulse 2.4s ease-out infinite",
          },
          "@keyframes radarIdlePulse": {
            "0%": { transform: "scale(1)", opacity: 0.8 },
            "100%": { transform: "scale(1.8)", opacity: 0 },
          },
        }}
      >
        <SensorsOff sx={{ fontSize: "1.15rem" }} />
      </Box>

      <Box>
        <Typography sx={{ fontSize: "0.72rem", fontWeight: 800, letterSpacing: "0.16em", color: lost ? warn : accent, lineHeight: 1.3 }}>
          {lost ? "SIGNAL LOST" : "NO RADAR SIGNAL"}
        </Typography>
        <Typography sx={{ fontSize: "0.72rem", color: alpha("#fff", 0.75), lineHeight: 1.3 }}>
          {lost
            ? `Last data at ${new Date(lastDataAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false })}`
            : "Waiting for data from the radar station…"}
        </Typography>
      </Box>
    </Box>
  );
};

// export default React.memo(RadarScan, () => true);
export default React.memo(RadarScan);
// export default RadarScan;
